<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'InvoiceLineGroup',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    invoice: {
      type: Object,
      required: true,
    },
    groupId: {
      type: String,
      required: false,
    },
    isDefaultGroup: {
      type: Boolean,
      required: false,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const formId = ref(null);
  const formName = ref('invoiceLineGroup');
  const expenseTypes = ref(null);
  const titles = ref({
    incoming: $fn.value.pgettext('Invoice', 'Create Incoming Invoice'),
    outgoing: $fn.value.pgettext('Invoice', 'Create Outgoing Invoice'),
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_7307096920626688_dataSource = computed(() => ({
    name: 'suggest_rental',
  }));
  const b_3119842801924129_dataSource = computed(() => ({
    name: 'suggest_property',
  }));
  const b_4994395583501828_dataSource = computed(() => ({
    name: 'suggest_worksheet',
  }));
  const b_2260153975480357_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'partner',
      contractors_only: true,
    },
  }));

  const $data = computed(() => ({
    item: item.value,
    formId: formId.value,
    formName: formName.value,
    expenseTypes: expenseTypes.value,
    titles: titles.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_7307096920626688_dataSource: b_7307096920626688_dataSource.value,
    b_3119842801924129_dataSource: b_3119842801924129_dataSource.value,
    b_4994395583501828_dataSource: b_4994395583501828_dataSource.value,
    b_2260153975480357_dataSource: b_2260153975480357_dataSource.value,
  }));

  const $$load_item = async () => {
    return props.groupId
      ? $fn.value.fetch('read_invoice_line_group', {
          asResource: true,
          parameters: { id: props.groupId },
        })
      : $fn.value.schemaDefaults('CreateInvoiceLineGroup', {
          invoice_id: props.invoice.id,
          is_default_group: props.isDefaultGroup,
        });
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_expenseTypes = async () => {
    return $fn.value
      .fetch('list_object_by_type', {
        parameters: { object_type: 'expense-type' },
      })
      .then((d) => _.orderBy(d, ['name']));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'expenseTypes' },
      { name: 'titles' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_7307096920626688_dataSource', nodeId: '7307096920626688' },
      { name: 'b_3119842801924129_dataSource', nodeId: '3119842801924129' },
      { name: 'b_4994395583501828_dataSource', nodeId: '4994395583501828' },
      { name: 'b_2260153975480357_dataSource', nodeId: '2260153975480357' },
    ],
    loaders: {
      item: $$load_item,
      formId: $$load_formId,
      expenseTypes: $$load_expenseTypes,
    },
    ux: {},
    dataRefs: {
      item,
      formId,
      formName,
      expenseTypes,
      titles,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_7307096920626688_dataSource,
      b_3119842801924129_dataSource,
      b_4994395583501828_dataSource,
      b_2260153975480357_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const incomingInvoice = computed(() => {
    return invoiceKind === 'incoming';
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          item.value.isNew &&
            $fn.value.pgettext('InvoiceLineGroup', 'New line group'),
          !item.value.isNew &&
            $fn.value.pgettext('InvoiceLineGroup', 'Edit line group'),
        ],
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.groupId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('groupId');
    }
  );
  watch(
    () => props.isDefaultGroup,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('isDefaultGroup');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_v1vv3ltqs_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_1lelxl837_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_jtdl6gucn_getActionArgs = async ($event) => {
    return {
      objectType: 'invoice_line_group',
      op: 'upsert',
      data: item.value,
    };
  };
  const act_2140198529698299_closeModal_1s9at6jtg_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        value: {
          deleted: true,
        },
      },
    };
  };
  const act_2167826738365259_closeModal_ljnfe7x4b_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_v1vv3ltqs =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_v1vv3ltqs_getActionArgs,
    });
  const inst_evh_7315092382398562_close_zng58o2wv =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_v1vv3ltqs,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_zng58o2wv],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_37d590exa =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_v248zdx7f =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_37d590exa,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_v248zdx7f],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_x6w0ybwzt =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_9slape78e =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_x6w0ybwzt,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_9slape78e],
    event: 'close',
  });
  const act_1040167445267876_closeModal_1lelxl837 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_1lelxl837_getActionArgs,
    });
  const inst_evh_1040167445267876_success_m2k13pint =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_1lelxl837,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_jtdl6gucn = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_jtdl6gucn_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_m2k13pint],
  });
  const inst_evh_1040167445267876_submit_g6t4a1rww =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_jtdl6gucn,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_g6t4a1rww],
    event: 'submit',
  });
  const act_2140198529698299_closeModal_1s9at6jtg =
    new actionTypes.CloseModalAction({
      actionArgs: act_2140198529698299_closeModal_1s9at6jtg_getActionArgs,
    });
  const inst_evh_2140198529698299_success_fjlfkk44h =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeModal_1s9at6jtg,
      event: 'success',
      displayName: 'closeModal',
    });
  const inst_evh_2140198529698299_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2140198529698299_success_fjlfkk44h],
    event: 'success',
  });
  const act_2167826738365259_closeModal_ljnfe7x4b =
    new actionTypes.CloseModalAction({
      actionArgs: act_2167826738365259_closeModal_ljnfe7x4b_getActionArgs,
    });
  const inst_evh_2167826738365259_clickPreventStop_kdv8wlclc =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeModal_ljnfe7x4b,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_kdv8wlclc],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_zng58o2wv,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_v248zdx7f,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_9slape78e,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_m2k13pint,
    inst_evh_1040167445267876_submit_g6t4a1rww,
    submit: inst_evh_1040167445267876_submit_g6t4a1rww,
    inst_evh_1040167445267876_submit,
    inst_evh_2140198529698299_success_fjlfkk44h,
    inst_evh_2140198529698299_success,
    inst_evh_2167826738365259_clickPreventStop_kdv8wlclc,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_zng58o2wv = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_zng58o2wv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_v248zdx7f = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_v248zdx7f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_9slape78e = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_9slape78e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_m2k13pint = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_m2k13pint.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_g6t4a1rww = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_g6t4a1rww.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_fjlfkk44h = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_fjlfkk44h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_kdv8wlclc = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_kdv8wlclc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="InvoiceLineGroup"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        autofocus="first"
        v-if="loaded"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <ItpFormGroup
            name="description"
            :label="pgettext('InvoiceLineGroup', 'Description')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpFormInput
              name="description"
              v-model="item.description"
              :value="item.description"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.description =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              :readonly="item.readonly"
              :placeholder="
                pgettext(
                  'InvoiceLineGroup/description/placeholder',
                  'Enter group description'
                )
              "
            >
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            name="expense_type"
            :label="pgettext('InvoiceLineGroup', 'Expense Type')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpFormSelect2
              name="expense_type"
              v-model="item.expense_type"
              :value="item.expense_type"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.expense_type =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              :readonly="item.readonly"
              clearable
              :options="expenseTypes"
              valueField="name"
              textField="name"
            >
            </ItpFormSelect2>
          </ItpFormGroup>
          <ItpFormGroup
            name="rental"
            :label="pgettext('InvoiceLineGroup', 'Rental')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpSelect2
              name="rental"
              v-model="item.rental"
              :value="item.rental"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.rental =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              bind-object
              :readonly="item.readonly"
              clearable
              :dataSource="b_7307096920626688_dataSource"
              optionTemplate="<UcSuggestRentalOption :option=option>
</UcSuggestRentalOption>"
              use-option-template-for-selected-option
            >
            </ItpSelect2>
          </ItpFormGroup>
          <ItpFormGroup
            name="property"
            :label="pgettext('InvoiceLineGroup', 'Property')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpSelect2
              name="property"
              v-model="item.property"
              :value="item.property"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.property =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              bind-object
              :readonly="item.readonly"
              clearable
              :dataSource="b_3119842801924129_dataSource"
              optionTemplate="<UcSuggestPropertyOption :option=option>
</UcSuggestPropertyOption>"
              use-option-template-for-selected-option
            >
            </ItpSelect2>
          </ItpFormGroup>
          <ItpFormGroup
            name="worksheet"
            :label="pgettext('InvoiceLineGroup', 'Worksheet')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpSelect2
              name="worksheet"
              v-model="item.worksheet"
              :value="item.worksheet"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.worksheet =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              bind-object
              :readonly="item.readonly"
              clearable
              :dataSource="b_4994395583501828_dataSource"
              optionTemplate="<UcSuggestWorksheetOption :option=option>
</UcSuggestWorksheetOption>"
              use-option-template-for-selected-option
            >
            </ItpSelect2>
          </ItpFormGroup>
          <ItpFormGroup
            name="contractor"
            :label="pgettext('InvoiceLineGroup', 'Contractor')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpSelect2
              name="contractor"
              v-model="item.contractor"
              :value="item.contractor"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.contractor =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              bind-object
              :readonly="item.readonly"
              clearable
              :dataSource="b_2260153975480357_dataSource"
              optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
              use-option-template-for-selected-option
            >
            </ItpSelect2>
          </ItpFormGroup>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <BtnDeleteResource
          :resource="item"
          variant="danger"
          size="md"
          class="me-auto"
          v-if="!item.isNew && !invoice.readonly && !item.readonly"
          @success="evh_2140198529698299_success($event, {})"
        >
        </BtnDeleteResource>
        <ItpButton
          :form="forms[formName] && forms[formName].id"
          icon="fa-save"
          :text="gettext('Save')"
          type="submit"
          variant="primary"
          :spinning="actions.submit.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
